import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../contexts/AuthContext';

const { FiCalendar, FiSave, FiCheck, FiMail, FiExternalLink, FiHelpCircle, FiSettings, FiCreditCard, FiLock, FiAlertCircle, FiRefreshCw } = FiIcons;

const SettingsPage = () => {
  const { user } = useAuth();
  
  // Google Calendar State
  const [calendarConfig, setCalendarConfig] = useState({
    clientId: '',
    clientSecret: '',
    calendarId: '',
    apiKey: ''
  });
  const [isSaved, setIsSaved] = useState(false);

  // Stripe Connect State
  const [stripeConnect, setStripeConnect] = useState({
    isConnected: false,
    accountId: '',
    payoutSchedule: 'manual', // 'manual' or 'daily'/'weekly'
    transferGroupEnabled: true // Enforced by platform
  });
  const [isConnecting, setIsConnecting] = useState(false);

  // Load mocks
  useEffect(() => {
    // Simulating fetching owner's Stripe status
    const savedStripe = localStorage.getItem(`stripe_connect_${user?.id}`);
    if (savedStripe) {
      setStripeConnect(JSON.parse(savedStripe));
    }
  }, [user]);

  // Handlers
  const handleConfigChange = (e) => {
    setCalendarConfig({ ...calendarConfig, [e.target.name]: e.target.value });
    setIsSaved(false);
  };

  const handleSaveConfig = (e) => {
    e.preventDefault();
    // In a real app, send this to your backend
    console.log('Saving Google Calendar Config:', calendarConfig);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 3000);
  };

  const handleStripeConnect = () => {
    setIsConnecting(true);
    // Simulate OAuth Redirect Flow
    setTimeout(() => {
      const newConfig = {
        isConnected: true,
        accountId: 'acct_1OuXyZ2eZvKYlo2C', // Mock Account ID
        payoutSchedule: 'daily',
        transferGroupEnabled: true
      };
      setStripeConnect(newConfig);
      localStorage.setItem(`stripe_connect_${user?.id}`, JSON.stringify(newConfig));
      setIsConnecting(false);
      alert('Stripe Account Connected Successfully!');
    }, 2000);
  };

  const handleStripeDisconnect = () => {
    if (window.confirm('Are you sure you want to disconnect your Stripe account? You will not be able to receive payments.')) {
        setStripeConnect({ ...stripeConnect, isConnected: false, accountId: '' });
        localStorage.removeItem(`stripe_connect_${user?.id}`);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-12 max-w-5xl mx-auto">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-600 mt-1">Manage your application settings and integrations</p>
          </div>
        </div>

        {/* --- Stripe Connect Section --- */}
        <div className="space-y-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-indigo-100 p-2 rounded-lg">
              <SafeIcon icon={FiCreditCard} className="h-6 w-6 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Payout Settings</h2>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
             <div className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row gap-8 items-start">
                    {/* Status Card */}
                    <div className="flex-1 w-full">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Stripe Connect Status</h3>
                        
                        {!stripeConnect.isConnected ? (
                            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
                                <div className="mx-auto bg-white p-3 rounded-full w-16 h-16 flex items-center justify-center shadow-sm mb-4">
                                    <SafeIcon icon={FiCreditCard} className="h-8 w-8 text-gray-400" />
                                </div>
                                <h4 className="font-bold text-gray-900 mb-2">Not Connected</h4>
                                <p className="text-sm text-gray-600 mb-6">
                                    Connect your Stripe account to receive payouts directly. 
                                    We use <strong>Stripe Connect</strong> to ensure secure and compliant transfers.
                                </p>
                                <button 
                                    onClick={handleStripeConnect}
                                    disabled={isConnecting}
                                    className="bg-[#635BFF] hover:bg-[#5851E0] text-white px-6 py-3 rounded-lg font-bold transition-all shadow-md hover:shadow-lg flex items-center justify-center w-full sm:w-auto mx-auto"
                                >
                                    {isConnecting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                            Connecting...
                                        </>
                                    ) : (
                                        <>
                                            Connect with Stripe <SafeIcon icon={FiExternalLink} className="ml-2 h-4 w-4" />
                                        </>
                                    )}
                                </button>
                            </div>
                        ) : (
                            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center">
                                        <div className="bg-green-100 p-2 rounded-full mr-3">
                                            <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-600" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-green-900">Connected</h4>
                                            <p className="text-xs text-green-700 font-mono">{stripeConnect.accountId}</p>
                                        </div>
                                    </div>
                                    <button 
                                        onClick={handleStripeDisconnect}
                                        className="text-red-600 hover:text-red-700 text-sm font-medium underline"
                                    >
                                        Disconnect
                                    </button>
                                </div>
                                
                                <div className="space-y-4 border-t border-green-100 pt-4">
                                    <div className="flex items-start gap-3">
                                        <SafeIcon icon={FiRefreshCw} className="h-5 w-5 text-green-600 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-bold text-gray-900">Automated Payouts</p>
                                            <p className="text-xs text-gray-600 mt-1">
                                                Funds are held in a secure <strong>Transfer Group</strong> and released to your account 24 hours after guest check-in.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <SafeIcon icon={FiLock} className="h-5 w-5 text-green-600 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-bold text-gray-900">Secure Processing</p>
                                            <p className="text-xs text-gray-600 mt-1">
                                                Payments are processed securely by Stripe. We do not store credit card details.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Info Side */}
                    <div className="w-full md:w-80 bg-blue-50 border border-blue-100 rounded-xl p-6">
                        <h4 className="font-bold text-blue-900 mb-4 flex items-center">
                            <SafeIcon icon={FiHelpCircle} className="mr-2 h-5 w-5" />
                            How it works
                        </h4>
                        <ul className="space-y-4 text-sm text-blue-800">
                            <li className="flex gap-3">
                                <span className="bg-blue-200 text-blue-800 font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs">1</span>
                                <span>Guest books your venue and pays via credit card.</span>
                            </li>
                            <li className="flex gap-3">
                                <span className="bg-blue-200 text-blue-800 font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs">2</span>
                                <span>Funds are securely held in a <strong>Stripe Transfer Group</strong> linked to the specific booking.</span>
                            </li>
                            <li className="flex gap-3">
                                <span className="bg-blue-200 text-blue-800 font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs">3</span>
                                <span>After successful check-in, the system automatically triggers the transfer to your connected account.</span>
                            </li>
                        </ul>
                    </div>
                </div>
             </div>
          </div>
        </div>

        <hr className="border-gray-200" />

        {/* --- Google Calendar Integration Section --- */}
        <div className="space-y-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-blue-100 p-2 rounded-lg">
              <SafeIcon icon={FiCalendar} className="h-6 w-6 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Google Calendar Integration</h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left Column: Calendar Instructions */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">How to Connect</h3>
                <div className="prose prose-sm text-gray-600">
                  <p className="mb-4">
                    Follow these steps to generate the necessary credentials from the Google Cloud Console. This allows VenueManager to sync your bookings automatically.
                  </p>
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center font-bold text-xs">1</span>
                      <div>
                        <p className="font-medium text-gray-900">Create a Project</p>
                        <p className="text-xs mt-1">
                          Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center">Google Cloud Console <SafeIcon icon={FiExternalLink} className="ml-1 h-3 w-3" /></a> and create a new project.
                        </p>
                      </div>
                    </div>
                    {/* Simplified instructions for brevity */}
                    <div className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center font-bold text-xs">2</span>
                      <div>
                        <p className="font-medium text-gray-900">Get Credentials</p>
                        <p className="text-xs mt-1">
                          Enable "Google Calendar API" in the library. Then create <strong>OAuth client ID</strong> credentials for a Web Application.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center font-bold text-xs">3</span>
                      <div>
                        <p className="font-medium text-gray-900">Get Calendar ID</p>
                        <p className="text-xs mt-1">
                          In Google Calendar settings, find the "Integrate calendar" section to get your <strong>Calendar ID</strong>.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Calendar Form */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-gray-900">Connection Configuration</h3>
                  {isSaved && (
                    <span className="text-green-600 text-sm font-medium flex items-center animate-fade-in">
                      <SafeIcon icon={FiCheck} className="mr-1 h-4 w-4" /> Saved
                    </span>
                  )}
                </div>
                <form onSubmit={handleSaveConfig} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Calendar ID <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input 
                        type="text" 
                        name="calendarId" 
                        required 
                        value={calendarConfig.calendarId}
                        onChange={handleConfigChange}
                        placeholder="e.g. yourname@gmail.com"
                        className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400">
                        <SafeIcon icon={FiMail} className="h-4 w-4" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client ID <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="text" 
                      name="clientId" 
                      required 
                      value={calendarConfig.clientId}
                      onChange={handleConfigChange}
                      placeholder="e.g. 123456789-abcde.apps.googleusercontent.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client Secret <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="password" 
                      name="clientSecret" 
                      required 
                      value={calendarConfig.clientSecret}
                      onChange={handleConfigChange}
                      placeholder="e.g. GOCSPX-xxxxxxxxxxxx"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Key <span className="text-gray-400 font-normal">(Optional, for public read access)</span>
                    </label>
                    <input 
                      type="text" 
                      name="apiKey" 
                      value={calendarConfig.apiKey}
                      onChange={handleConfigChange}
                      placeholder="AIzaSy..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                    />
                  </div>
                  <div className="pt-4 border-t border-gray-100">
                    <button 
                      type="submit" 
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center"
                    >
                      <SafeIcon icon={FiSave} className="mr-2 h-4 w-4" /> Save Calendar Config
                    </button>
                  </div>
                </form>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <SafeIcon icon={FiHelpCircle} className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-yellow-800 text-sm">Testing your connection</h4>
                    <p className="text-xs text-yellow-700 mt-1">
                      After saving, try making a test booking on one of your venue widgets.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;